import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Editor from '@/pages/Editor';
import { ChunkErrorBoundary } from '@/components/ChunkErrorBoundary';
import {
  EDITOR_VISIBILITY_EVENT,
  readSnapOperationActive,
  SNAP_OPERATION_EVENT,
} from '@/editor/utils/snapOperation';
import { SESSION_DIRTY_KEY } from '@/editor/utils/editorSession';

const EDITOR_IDLE_UNMOUNT_MS = 5 * 60 * 1000;
const EDITOR_IDLE_RECHECK_MS = 60 * 1000;

function readEditorSessionDirty(): boolean {
  try {
    return window.localStorage.getItem(SESSION_DIRTY_KEY) === '1';
  } catch {
    return true;
  }
}

/**
 * Keeps the editor page mounted (including its iframe) when navigating to other
 * app routes such as /dashboard, so returning to /editor is instant.
 */
export default function AppWithEditorKeepAlive() {
  const location = useLocation();
  const isEditor =
    location.pathname === '/editor' || location.pathname.startsWith('/editor/');
  const [editorWarm, setEditorWarm] = useState(isEditor);
  const idleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (isEditor) {
      setEditorWarm(true);
    }
  }, [isEditor]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(EDITOR_VISIBILITY_EVENT, {
        detail: { visible: isEditor },
      }),
    );
  }, [isEditor, editorWarm]);

  useEffect(() => {
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    if (!editorWarm || isEditor) {
      return;
    }

    const schedule = (delay: number) => {
      idleTimerRef.current = window.setTimeout(() => {
        idleTimerRef.current = null;
        if (!readEditorSessionDirty() && !readSnapOperationActive()) {
          setEditorWarm(false);
          return;
        }
        schedule(EDITOR_IDLE_RECHECK_MS);
      }, delay);
    };

    schedule(EDITOR_IDLE_UNMOUNT_MS);

    return () => {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [editorWarm, isEditor]);

  useEffect(() => {
    if (isEditor || !editorWarm) {
      return;
    }
    const handleOperationChange = () => {
      if (readSnapOperationActive()) {
        return;
      }
      if (idleTimerRef.current === null && !readEditorSessionDirty()) {
        idleTimerRef.current = window.setTimeout(() => {
          idleTimerRef.current = null;
          if (!readEditorSessionDirty() && !readSnapOperationActive()) {
            setEditorWarm(false);
          }
        }, EDITOR_IDLE_RECHECK_MS);
      }
    };
    window.addEventListener(SNAP_OPERATION_EVENT, handleOperationChange as EventListener);
    return () => {
      window.removeEventListener(SNAP_OPERATION_EVENT, handleOperationChange as EventListener);
    };
  }, [editorWarm, isEditor]);

  return (
    <>
      {editorWarm ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: isEditor ? 40 : -1,
            visibility: isEditor ? 'visible' : 'hidden',
            pointerEvents: isEditor ? 'auto' : 'none',
          }}
          aria-hidden={!isEditor}
        >
          <ChunkErrorBoundary>
            <Editor />
          </ChunkErrorBoundary>
        </div>
      ) : null}
      {!isEditor ? <Outlet /> : null}
    </>
  );
}
